/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FC, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { Box, Flex } from 'components/styled';
import LoaderSpin from 'components/loaders/LoaderSpin';
import Button from 'components/styled/Button';

import { useErrorTranslation } from 'utils/localization';

import ErrorFeedback from '../error/ErrorFeedback';

import { Input, InputWrapper, Label, TextInputProps } from './TextInput';

const toggleLanguage = (lang: 'cz' | 'en') => (lang === 'cz' ? 'en' : 'cz');

const MultistringInput: FC<
	TextInputProps & { languageSwitchHidden?: boolean }
> = ({
	name,
	type = 'text',
	label,
	required,
	iconLeft,
	iconRight,
	loading,
	colorVariant,
	languageSwitchHidden,
	...inputProps
}) => {
	const tError = useErrorTranslation();
	const [fieldCzech, metaCzech] = useField<string>(`${name}.CZECH`);
	const [fieldEnglish, metaEnglish] = useField<string>(`${name}.ENGLISH`);
	const { isSubmitting } = useFormikContext();

	const { i18n } = useTranslation();
	const [lang, setLang] = useState<'cz' | 'en'>(i18n.language as never);

	return (
		<Flex
			width={1}
			flexDirection="column"
			color={colorVariant === 'inverted' ? 'white' : 'text'}
		>
			<Flex>
				{label && (
					<Label
						htmlFor={lang === 'cz' ? `${name}.CZECH` : `${name}.ENGLISH`}
						required={required}
					>
						{label}:
					</Label>
				)}
				<Box flexGrow={1} />
				{!languageSwitchHidden && (
					<Button
						aria-label={lang === 'cz' ? 'english ' + label : 'čeština ' + label}
						variant="text"
						py={0}
						mb={2}
						color={
							(lang === 'cz' && !!metaEnglish.error && metaEnglish.touched) ||
							(!!metaCzech.error && metaCzech.touched)
								? 'error'
								: 'primary'
						}
						onClick={() => setLang(toggleLanguage)}
						css={css`
							text-transform: uppercase;
						`}
					>
						{toggleLanguage(lang)}
					</Button>
				)}
			</Flex>
			<InputWrapper
				hasError={
					!!metaCzech.error &&
					metaCzech.touched &&
					!!metaEnglish.error &&
					metaEnglish.touched
				}
				inverted={colorVariant === 'inverted'}
				backgroundColor="white"
			>
				{iconLeft && (
					<Box mr={2} flex={0} minWidth="auto" color="primary">
						{iconLeft}
					</Box>
				)}

				<Input
					type={type}
					{...(lang === 'cz' ? fieldCzech : fieldEnglish)}
					{...inputProps}
					disabled={isSubmitting || inputProps.disabled}
				/>

				{iconRight && (
					<Box ml={2} flex={0} minWidth="auto" color="primary">
						{iconRight}
					</Box>
				)}

				{loading && (
					<Box ml={1} flex={0} minWidth="auto" color="primary">
						<LoaderSpin size={23} />
					</Box>
				)}
			</InputWrapper>

			{lang === 'cz'
				? !!metaCzech.error &&
				  metaCzech.touched && (
						<ErrorFeedback>{tError(metaCzech.error)}</ErrorFeedback>
				  )
				: !!metaEnglish.error &&
				  metaEnglish.touched && (
						<ErrorFeedback>{tError(metaEnglish.error)}</ErrorFeedback>
				  )}
		</Flex>
	);
};

export default MultistringInput;
